import React, { useState } from 'react';

export function SendTemplateModal({ isOpen, onClose, template, onSend }) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [variableValues, setVariableValues] = useState({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen || !template) return null;

  const handleVariableChange = (position, value) => {
    setVariableValues(prev => ({ ...prev, [position]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate phone number
    if (!phoneNumber.trim()) {
      setError('יש להזין מספר טלפון');
      return;
    }

    // Validate all variables are filled
    const variables = template.variables || [];
    for (const variable of variables) {
      if (!variableValues[variable.position]?.trim()) {
        setError(`יש למלא את השדה: ${variable.name}`);
        return;
      }
    }

    try {
      setSending(true);
      await onSend({
        phoneNumber,
        templateId: template.machineName,
        contentSid: template.twilioContentSid,
        variables: variableValues
      });

      // Reset form
      setPhoneNumber('');
      setVariableValues({});
      onClose();
    } catch (err) {
      setError(err.message || 'אירעה שגיאה בשליחת ההודעה');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">שלח תבנית: {template.displayName}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium mb-1">מספר טלפון</label>
            <input
              type="tel"
              className="w-full rounded-xl border px-3 py-2"
              placeholder="050-1234567 או +972501234567"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              dir="ltr"
            />
            <p className="mt-1 text-xs text-gray-500">
              ניתן להזין מספר ישראלי או בפורמט בינלאומי
            </p>
          </div>

          {/* Template Preview */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="text-sm font-medium mb-2">תצוגה מקדימה של התבנית</h3>
            {template.headerType === 'text' && template.headerText && (
              <p className="font-semibold text-gray-900 mb-2">{template.headerText}</p>
            )}
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{template.textRaw}</p>
            {template.buttons && template.buttons.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {template.buttons.map((btn, idx) => (
                  <span key={idx} className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800">
                    {btn.type === 'URL' && '🔗'}
                    {btn.type === 'PHONE' && '📞'}
                    {btn.type === 'QUICK_REPLY' && '💬'}
                    {' '}{btn.title}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Variables */}
          {template.variables && template.variables.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium">ערכי משתנים</h3>
              {template.variables.map((variable) => (
                <div key={variable.position}>
                  <label className="block text-sm font-medium mb-1">
                    {variable.name}
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-xl border px-3 py-2"
                    placeholder={variable.placeholder || variable.name}
                    value={variableValues[variable.position] || ''}
                    onChange={(e) => handleVariableChange(variable.position, e.target.value)}
                  />
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={sending}
              className="flex-1 rounded-xl bg-indigo-600 text-white px-4 py-2 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? 'שולח...' : 'שלח הודעה'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-300 px-4 py-2 hover:bg-gray-50"
            >
              ביטול
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
